import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ApiError } from '../../lib/api';
import { sendDirectTestLead, type DirectTestLeadBody } from './api';

// SEND TEST LEAD (Settings -> Speed to Lead -> club). Kyck admins only (the host renders it only for them and the server
// refuses anyone else). ⚠️ It does NOT go through the relay or the club's CRM provider: it runs the ordinary lead intake with
// a synthetic lead, so Sam and the calls behave as for a real one without putting a bad request on a CRM API. Nothing fires
// on render; the only request is the button.

const SOURCES: Array<{ value: DirectTestLeadBody['source']; label: string }> = [
  { value: 'meta_lead_form', label: 'Meta lead form' },
  { value: 'website', label: 'Website' },
  { value: 'phone', label: 'Phone' },
];

export function DirectTestLeadPanel({ clubId }: { clubId: string }) {
  const [name, setName] = useState('TEST');
  const [mobile, setMobile] = useState('');
  const [source, setSource] = useState<DirectTestLeadBody['source']>('meta_lead_form');
  const [startFresh, setStartFresh] = useState(true);
  const [result, setResult] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: () => sendDirectTestLead(clubId, { name: name.trim() || undefined, mobile: mobile.trim(), source, startFresh }),
    onSuccess: (r) => {
      const closed = r.closedConversations > 0 ? ` ${r.closedConversations} open conversation${r.closedConversations === 1 ? '' : 's'} closed first.` : '';
      setResult(`${r.intake?.detail ?? 'Test lead created; intake has not finished yet and will be retried.'}${closed}`);
      toast.success('Test lead sent through intake.');
    },
    onError: (err) => {
      setResult(null);
      toast.error(err instanceof ApiError || err instanceof Error ? err.message : 'Something went wrong.');
    },
  });

  return (
    <div className="space-y-3 rounded-md border border-slate-200 p-4" data-testid="direct-test-lead">
      <h3 className="text-sm font-medium text-slate-800">Send test lead</h3>
      <p className="text-xs text-slate-500">
        Runs a made-up lead through this club&apos;s Speed to Lead and AI SMS exactly as a real one would. It is <strong>not</strong> sent to the club&apos;s CRM. The mobile must already be on the club bot&apos;s test allowlist, because a test lead texts and rings a real phone. It is marked Test everywhere and left out of the reports.
      </p>
      <div className="grid gap-3 sm:grid-cols-3">
        <label className="block text-sm text-slate-700">
          <span className="mb-1 block">Name</span>
          <Input value={name} onChange={(e) => setName(e.target.value)} aria-label="Test lead name" />
        </label>
        <label className="block text-sm text-slate-700">
          <span className="mb-1 block">Mobile</span>
          <Input value={mobile} placeholder="e.g. 0412 345 678" onChange={(e) => setMobile(e.target.value)} aria-label="Test lead mobile" />
        </label>
        <label className="block text-sm text-slate-700">
          <span className="mb-1 block">Source</span>
          <select className="w-full rounded-md border border-slate-300 bg-white p-2 text-sm" value={source} onChange={(e) => setSource(e.target.value as DirectTestLeadBody['source'])} aria-label="Test lead source">
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      </div>
      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" className="h-4 w-4" checked={startFresh} onChange={(e) => setStartFresh(e.target.checked)} />
        Start fresh (close this number&apos;s open conversation at this club first)
      </label>
      <Button type="button" variant="outline" disabled={send.isPending || mobile.trim() === ''} onClick={() => send.mutate()}>
        {send.isPending ? 'Sending…' : 'Send test lead'}
      </Button>
      {result && <p role="status" data-testid="direct-test-result" className="rounded bg-slate-50 p-2 text-xs text-slate-700">{result}</p>}
    </div>
  );
}
